import { useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Plus, Search } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { Checkbox } from '@/components/ui';
import { FAVORITE_MENU_MAX } from '@/stores/useFavoriteMenus';
import { PERMISSION_MENU_GROUPS, toMenuPickBlocks, type FavoriteMenuCandidate, type MenuPickEntry } from './Sidebar';

/**
 * QA-509 즐겨찾는 메뉴 고르기 — 사이드바처럼 그룹 → 하위 묶음 → 메뉴 단계로 체크박스를 보여 주고(메뉴 검색 포함)
 * 최대 7개까지 고른다. 이 관리자가 볼 수 있는 메뉴(역할 권한 + QA-514 역할별 접근 메뉴)만 나온다. 체크를 풀면 바로 빠진다.
 */
export const FavoriteMenuPicker = ({
  candidates,
  selected,
  onToggle,
}: {
  /** 이 관리자가 볼 수 있는 메뉴. */
  candidates: ReadonlyArray<FavoriteMenuCandidate>;
  /** 지금 즐겨찾기(순서). */
  selected: ReadonlyArray<string>;
  onToggle: (to: string, on: boolean) => void;
}) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const full = selected.length >= FAVORITE_MENU_MAX;

  const groups = useMemo(() => {
    const allowed = new Set(candidates.map((c) => c.to));
    const q = query.trim().toLowerCase();
    const hit = (key: string) => String(t(key)).toLowerCase().includes(q);
    return PERMISSION_MENU_GROUPS.map((g) => {
      const heading = String(t(g.headingKey));
      const headingHit = q !== '' && heading.toLowerCase().includes(q);
      // 메뉴 이름·하위 묶음 이름·그룹 이름 어느 것으로도 찾는다.
      const entries = g.entries.filter(
        (e) => allowed.has(e.to) && (q === '' || headingHit || hit(e.labelKey) || (e.subgroup ? hit(e.subgroup.labelKey) : false)),
      );
      return { headingKey: g.headingKey, heading, blocks: toMenuPickBlocks(entries), count: entries.length };
    }).filter((g) => g.count > 0);
  }, [candidates, query, t]);

  const MenuRow = ({ entry, level }: { entry: MenuPickEntry; level: 1 | 2 }) => {
    const on = selected.includes(entry.to);
    const disabled = !on && full;
    const Icon = entry.icon;
    return (
      <label
        className={`flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-[color:var(--surface-1)] ${level === 2 ? 'text-[12px]' : 'text-[13px]'}`}
        style={{ color: disabled ? 'var(--content-soft)' : 'var(--content-default)', cursor: disabled ? 'not-allowed' : 'pointer' }}
        title={disabled ? `즐겨찾는 메뉴는 ${FAVORITE_MENU_MAX}개까지입니다` : undefined}
      >
        <Checkbox
          checked={on}
          disabled={disabled}
          onCheckedChange={(v) => onToggle(entry.to, v === true)}
          data-testid={`favorite-menu-option-${entry.to}`}
        />
        {level === 1 ? <Icon size={14} aria-hidden="true" /> : null}
        <span className="truncate">{t(entry.labelKey)}</span>
      </label>
    );
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setQuery('');
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="즐겨찾는 메뉴 고르기"
          className="flex h-8 shrink-0 items-center gap-1 rounded-full px-2.5 text-[12px] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
          style={{ border: '1px dashed var(--divider-strong)', color: 'var(--content-muted)' }}
          data-testid="favorite-menu-add"
        >
          <Plus size={13} aria-hidden="true" />
          {selected.length === 0 ? '즐겨찾는 메뉴' : null}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 flex w-[300px] flex-col rounded-lg shadow-lg outline-none"
          style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)' }}
          data-testid="favorite-menu-picker"
        >
          <div className="flex items-center justify-between px-3 pb-2 pt-3">
            <span className="text-[13px] font-semibold" style={{ color: 'var(--content-strong)' }}>
              즐겨찾는 메뉴
            </span>
            <span
              className="text-[12px] tabular-nums"
              style={{ color: full ? 'var(--accent-default)' : 'var(--content-muted)' }}
              data-testid="favorite-menu-count"
            >
              {selected.length}/{FAVORITE_MENU_MAX}
            </span>
          </div>
          {/* 사이드바 [메뉴 검색]과 같은 모양. */}
          <div className="px-3 pb-2">
            <div className="relative">
              <Search
                size={13}
                aria-hidden="true"
                className="absolute left-2.5 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--content-muted)' }}
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={String(t('sidebar.search', '메뉴 검색...'))}
                aria-label="메뉴 검색"
                autoFocus
                className="w-full rounded-md py-1.5 pl-7 pr-2.5 text-[12px] outline-none focus:[box-shadow:var(--focus-ring-brand)]"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)', color: 'var(--content-default)' }}
                data-testid="favorite-menu-search"
              />
            </div>
          </div>
          <div className="max-h-[400px] overflow-y-auto px-2 pb-2">
            {groups.length === 0 ? (
              <p className="px-2 py-4 text-center text-[12px]" style={{ color: 'var(--content-muted)' }}>
                찾는 메뉴가 없습니다.
              </p>
            ) : (
              groups.map((g, gi) => (
                // 1단계 그룹 — 그룹 사이 구분선, 진한 제목.
                <div
                  key={g.headingKey}
                  className="pb-1"
                  style={gi > 0 ? { borderTop: '1px solid var(--divider-strong)', marginTop: 4, paddingTop: 4 } : undefined}
                  data-testid={`favorite-menu-group-${g.headingKey}`}
                >
                  <p className="px-2 pb-1 pt-1.5 text-[12px] font-semibold" style={{ color: 'var(--content-strong)' }}>
                    {g.heading}
                  </p>
                  {g.blocks.map((block) =>
                    block.kind === 'entry' ? (
                      <MenuRow key={block.entry.to} entry={block.entry} level={1} />
                    ) : (
                      // 2단계 하위 묶음(예: 주문 관리 > 송장 일괄등록) — 묶음 이름 아래 들여 쓴 메뉴.
                      <div key={block.labelKey} className="mt-0.5" data-testid={`favorite-menu-subgroup-${block.labelKey}`}>
                        <p className="flex items-center gap-1.5 px-2 py-1 text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                          <block.icon size={13} aria-hidden="true" />
                          {t(block.labelKey)}
                        </p>
                        <div className="ml-4 pl-1" style={{ borderLeft: '1px solid var(--divider-strong)' }}>
                          {block.entries.map((e) => (
                            <MenuRow key={e.to} entry={e} level={2} />
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              ))
            )}
          </div>
          {full ? (
            <p className="px-3 pb-3 text-[11px]" style={{ color: 'var(--content-muted)' }}>
              {FAVORITE_MENU_MAX}개를 모두 골랐습니다. 다른 메뉴를 넣으려면 하나를 풀어 주세요.
            </p>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
